// @ts-nocheck
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  DialogContent,
  Dialog,
  DialogTitle,
  Typography,
  Grid,
  Tooltip,
  Autocomplete,
  TextField,
} from '@mui/material';
import { ReactComponent as CloseIcon } from '../../../../assets/Icons/lucideIcons/x.svg';
import { ReactComponent as FullscreenIcon } from '../../../../assets/Icons/lucideIcons/maximize.svg';
import { ReactComponent as FullscreenExitIcon } from '../../../../assets/Icons/lucideIcons/minimize.svg';

import { useSelector } from 'react-redux';
import {
  ReactMuiTableV8,
  ReactMuiTableColumnHeaderTextEllipsis,
} from '@nuva-org/solytics-frontend';
import PropTypes from 'prop-types';

import useModalDisclosure from '../../../../hooks/useModalDisclosure';
import { useDialogFullscreen } from '../../../../hooks/useDialogFullscreen';
import SearchTextField from '../../../../components/Common/SearchTextField';
import NoDataPresentComponent from '../../NoDataPresentComponent';
import AssociationFieldTableViewComponentSkeleton from '../../../Skeleton/AssociationFieldTableViewComponentSkeleton';
import {
  createDynamicColumns,
  getTableColumns,
  isValueEmpty,
} from '../../../../utils/Utils';
import {
  TABLE_CONTAINER_BOX_SX,
  TOOLBAR_ROW_SX,
  TOOLBAR_SEARCH_SX,
} from '../../../../const/tableToolbarStyles';
import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import {
  useDownstreamLinkedEntitiesTableQuery,
  useUpstreamEntitiesQuery,
} from '../../../../hooks/ModelInventory/useAsscoiationTableFieldQueries';
import { useAllAvailableAttributes } from '../../../../features/inventory/EntityDetails/UIModelDetails/queries/useEntityDetailsQuery';
import TableTextCell from '../../Tables/TableTextCell';
import { getEntityDetailsRoutePath } from '../../../../utils/inventoryRouteEngine';

// const ENTITY_TYPE = 'ModelAssociation'; //will be replaced with entitytypeid after core sync

const SEARCH_DEBOUNCE_MS = 300;

const INITIAL_PAGE = 1;
const INITIAL_SIZE = 5;

const renderLinkedEntityCell = ({
  dataArray,
  isRedirectionEnabled = false,
}) => {
  return (
    <TableTextCell
      entries={dataArray}
      isRedirectionEnabled={isRedirectionEnabled}
    />
  );
};
renderLinkedEntityCell.propTypes = {
  dataArray: PropTypes.arrayOf(PropTypes.string).isRequired,
  isRedirectionEnabled: PropTypes.bool,
};

const parseDerivedFormulaDirection = (derivedFormula) => {
  const direction = derivedFormula.includes('"direction": "upstream"')
    ? 'upstream'
    : 'downstream';
  return direction;
};

const getUpstreamOptionLabel = (option) => option?.label || '';

const isUpstreamOptionEqualToValue = (option, value) =>
  option?.entity_id === value?.entity_id;

// ─── Component Definition ──────────────────────────────────────────────────────────

const AssociationFieldTableViewComponent = ({
  attribute = {},
  entityId = null,
  entityData = null,
}) => {
  const {
    open: openPreview,
    handleOpen: openModal,
    handleClose: closeModal,
  } = useModalDisclosure();
  const { isFullScreen, toggleFullScreen, exitFullScreen } =
    useDialogFullscreen();

  const [selectedUpstreamEntity, setSelectedUpstreamEntity] = useState(null);

  const { pluginConfig, entityTypes: tenantEntityTypes } = useSelector(
    (state) => state?.tenant || {}
  );
  const isFCBEnabled = useMemo(() => !!pluginConfig?.FCB, [pluginConfig]);

  const handleClosePreview = useCallback(() => {
    closeModal();
    setSelectedUpstreamEntity(null);
  }, [closeModal]);

  const handleOpenPreview = useCallback(() => {
    openModal();
  }, [openModal]);

  const { activeTemplateType, attributeId, direction } = useMemo(() => {
    const dynamicEntityTypeId =
      entityData?.entity_type_id ||
      entityData?.EntityType ||
      attribute?.association_template?.template_type;

    const id = attribute?.id || '';
    const derivedFormulaDirection = parseDerivedFormulaDirection(
      attribute?.derived_formula
    );

    return {
      activeTemplateType: dynamicEntityTypeId,
      attributeId: id,
      direction: derivedFormulaDirection,
    };
  }, [attribute, entityId, entityData]);

  const isUpstream = direction === 'upstream';

  // ─── Queries ─────────────────────────────────────────────────────────────────────

  const { data: upstreamEntitiesData, isLoading: isUpstreamLoading } =
    useUpstreamEntitiesQuery(
      {
        entityId,
        attributeId,
      },
      {
        enabled: isUpstream && Boolean(attributeId) && Boolean(openPreview),
      }
    );

  const upstreamEntities = useMemo(() => {
    const rawEntities = upstreamEntitiesData?.upstream_entities || [];
    return rawEntities
      .filter((item) => {
        if (isFCBEnabled) {
          const typeId = item?.entity_type_id;
          const matchedEntityType = tenantEntityTypes?.find(
            (tenantEntityType) => tenantEntityType?.value === typeId
          );
          const requestType =
            item?.request_type || matchedEntityType?.request_type;
          return Boolean(matchedEntityType) && requestType === 'Normal';
        }
        return true;
      })
      .map((item) => {
        const alias = item?.entity_alias || '';
        const uid = item?.unique_id || '';
        const label = alias && uid ? `${alias} (${uid})` : alias || uid || '';
        return {
          ...item,
          label,
        };
      });
  }, [upstreamEntitiesData, isFCBEnabled, tenantEntityTypes]);

  useEffect(() => {
    if (openPreview && isUpstream && upstreamEntities.length === 1) {
      setSelectedUpstreamEntity(upstreamEntities[0]);
    }
  }, [openPreview, isUpstream, upstreamEntities]);

  const selectedUpstreamEntityId = selectedUpstreamEntity?.entity_id || null;

  const isQueryEnabled = isUpstream
    ? Boolean(entityId) &&
      Boolean(attributeId) &&
      Boolean(openPreview) &&
      Boolean(selectedUpstreamEntityId)
    : Boolean(entityId) && Boolean(attributeId) && Boolean(openPreview);

  const { data: columnsHeaderData } = useAllAvailableAttributes({
    params: {
      entity_id: entityId,
      entity_type: activeTemplateType,
      attribute_id: attributeId,
    },
    options: {
      enabled: isQueryEnabled,
    },
  });

  const { data: tableData, isLoading: isTableLoading } =
    useDownstreamLinkedEntitiesTableQuery(
      {
        entityId,
        page: INITIAL_PAGE,
        pageSize: INITIAL_SIZE,
        attributeId,
        ...(isUpstream && selectedUpstreamEntityId
          ? { parentEntityId: selectedUpstreamEntityId }
          : {}),
      },
      { enabled: isQueryEnabled }
    );

  // ─── Search state ────────────────────────────────────────────────────────────────

  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const handleSearchChange = useCallback(
    (e) => {
      setSearchTerm(e?.target?.value || '');
    },
    [setSearchTerm]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Raw rows as returned by the API, before any client-side filtering.
  const rawTableData = useMemo(() => {
    if (Array.isArray(tableData)) return tableData;
    return [];
  }, [tableData]);

  // Frontend-only search filtering — no refetching, just narrowing what's already fetched.
  const filteredTableData = useMemo(() => {
    if (!debouncedSearchTerm) return rawTableData;
    return rawTableData.filter((row) =>
      JSON.stringify(row)
        .toLowerCase()
        .includes(debouncedSearchTerm.toLowerCase())
    );
  }, [rawTableData, debouncedSearchTerm]);

  const getRouteEntityLabel = useCallback(
    (entity) => {
      const typeId = entity?.entity_type_id;
      const matched = tenantEntityTypes?.find(
        (tenantEntityType) => tenantEntityType?.value === typeId
      );
      return matched?.label || undefined;
    },
    [tenantEntityTypes]
  );

  const hardCodedColumns = useMemo(
    () => [
      {
        id: 'linked_entity_id',
        heading: 'Linked Entity ID',
        accessor: 'linked_entity',
        Cell: ({ row }) => {
          const entities = row?.original?.linked_entities_details || [];
          const dataArray = entities
            .map((entity) => {
              const id = entity?.unique_id;
              const entityRouteType = getRouteEntityLabel(entity);
              return {
                label: id,
                value:
                  id && entityRouteType
                    ? getEntityDetailsRoutePath(entityRouteType, id)
                    : '',
              };
            })
            .filter((item) => item.label);

          return renderLinkedEntityCell({
            dataArray,
            isRedirectionEnabled: true,
          });
        },
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        width: 200,
      },
      {
        id: 'linked_entity_name',
        heading: 'Linked Entity Name',
        accessor: 'linked_entity',
        Cell: ({ row }) => {
          const entities = row?.original?.linked_entities_details || [];
          const dataArray = entities
            .map((entity) => entity?.entity_name)
            .filter(Boolean);

          return renderLinkedEntityCell({
            dataArray,
          });
        },
        Header: ReactMuiTableColumnHeaderTextEllipsis,
        width: 200,
      },
    ],
    [getRouteEntityLabel]
  );

  const columns = useMemo(() => {
    if (isValueEmpty(columnsHeaderData)) return [];

    const dynamicColumns = createDynamicColumns({
      data: getTableColumns(columnsHeaderData),
      width: 200,
      HeaderCell: ReactMuiTableColumnHeaderTextEllipsis,
      entityType: activeTemplateType,
    });

    return [...hardCodedColumns, ...dynamicColumns];
  }, [columnsHeaderData, activeTemplateType, hardCodedColumns]);

  const handleGetRowProps = useCallback(
    (row) => ({
      onDoubleClick: () => {
        const targetId = row?.original?.unique_id;

        if (!targetId) return;

        const entityRouteType = getRouteEntityLabel(row?.original);

        if (entityRouteType) {
          const url = getEntityDetailsRoutePath(entityRouteType, targetId);
          if (url) {
            window.open(url, '_blank');
          }
        }
      },
      style: {
        cursor: 'pointer',
      },
    }),
    [getRouteEntityLabel]
  );

  // ─── Upstream Selector Rendering ─────────────────────────────────────────────────

  const handleUpstreamEntityChange = useCallback((_event, newValue) => {
    setSelectedUpstreamEntity(newValue);
  }, []);

  const renderUpstreamInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label="Select Upstream Entity"
        placeholder="Choose an entity to view its linked records"
        InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
      />
    ),
    []
  );

  const renderUpstreamSelector = useCallback(() => {
    if (!isUpstream) return null;

    if (isUpstreamLoading) return null;

    if (upstreamEntities.length === 0) {
      return (
        <Box
          display="flex"
          sx={{ height: '200px' }}
          flexGrow={1}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          <NoDataPresentComponent message="No upstream entities found for this attribute." />
        </Box>
      );
    }

    return (
      <Box mb={2}>
        {upstreamEntities.length > 1 && (
          <Autocomplete
            options={upstreamEntities}
            value={selectedUpstreamEntity}
            onChange={handleUpstreamEntityChange}
            getOptionLabel={getUpstreamOptionLabel}
            isOptionEqualToValue={isUpstreamOptionEqualToValue}
            size="small"
            disableClearable={!selectedUpstreamEntity}
            popupIcon={<DropdownArrow />}
            renderInput={renderUpstreamInput}
          />
        )}
      </Box>
    );
  }, [
    isUpstream,
    isUpstreamLoading,
    upstreamEntities,
    selectedUpstreamEntity,
    handleUpstreamEntityChange,
    renderUpstreamInput,
  ]);

  // ─── Table Content Rendering ─────────────────────────────────────────────────────

  const renderTableContent = useCallback(() => {
    if (isUpstream && isUpstreamLoading) {
      return <AssociationFieldTableViewComponentSkeleton />;
    }

    if (isUpstream && !isUpstreamLoading && upstreamEntities.length === 0) {
      return null;
    }

    if (
      isUpstream &&
      upstreamEntities.length > 1 &&
      !selectedUpstreamEntityId
    ) {
      return (
        <Box
          display="flex"
          sx={{ height: '200px' }}
          flexGrow={1}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          <NoDataPresentComponent message="Select an upstream entity above to view its linked records." />
        </Box>
      );
    }

    if (isTableLoading) {
      return <AssociationFieldTableViewComponentSkeleton />;
    }

    if (isValueEmpty(rawTableData)) {
      const emptyMessage = isUpstream
        ? 'No linked records found for the selected entity.'
        : `No ${attribute?.label || 'records'} found.`;

      return (
        <Box
          display="flex"
          sx={{ height: '200px' }}
          flexGrow={1}
          alignItems="center"
          flexDirection="column"
          justifyContent="center"
        >
          <NoDataPresentComponent message={emptyMessage} />
        </Box>
      );
    }

    return (
      <>
        <Box
          sx={{
            width: '300px',
            ...TOOLBAR_ROW_SX,
            pl: 0,
            pr: 0,
          }}
        >
          <SearchTextField
            value={searchTerm}
            onChange={handleSearchChange}
            placeholder="Search"
            sx={TOOLBAR_SEARCH_SX}
          />
        </Box>
        <Box sx={TABLE_CONTAINER_BOX_SX}>
          <ReactMuiTableV8
            data={filteredTableData}
            columns={columns}
            enablePagination
            initialPageSize={INITIAL_SIZE}
            rowsPerPageOptions={[5, 10, 15]}
            showOptions
            stickyHeader
            getRowProps={handleGetRowProps}
          />
        </Box>
      </>
    );
  }, [
    isUpstream,
    isUpstreamLoading,
    upstreamEntities.length,
    selectedUpstreamEntityId,
    isTableLoading,
    rawTableData,
    filteredTableData,
    columns,
    searchTerm,
    handleSearchChange,
    attribute?.label,
    handleGetRowProps,
  ]);

  return (
    <Grid container size={12}>
      <Grid size={12}>
        <Typography
          variant="body2"
          component="span"
          onClick={handleOpenPreview}
          sx={{
            color: 'primary.main',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          View {attribute?.label || 'Entity Association Table'}
        </Typography>
      </Grid>

      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        maxWidth={isFullScreen ? false : 'lg'}
        fullWidth
        fullScreen={isFullScreen}
        slotProps={{
          transition: {
            onExited: () => exitFullScreen(),
          },
          paper: {
            sx: {
              maxHeight: isFullScreen ? '100vh' : '90vh',
              height: isFullScreen ? '100vh' : 'auto',
            },
          },
        }}
      >
        <DialogTitle>
          <Box component="span" aria-live="polite" aria-atomic="true">
            {attribute?.label}
          </Box>
        </DialogTitle>

        {/* Fullscreen Button */}
        <Tooltip title={isFullScreen ? 'Exit Full Screen' : 'Full Screen'}>
          <IconButton
            color="primary"
            aria-label={isFullScreen ? 'Exit full screen' : 'Enter full screen'}
            onClick={toggleFullScreen}
            sx={{
              position: 'absolute',
              right: 56,
              top: 8,
            }}
          >
            {isFullScreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
          </IconButton>
        </Tooltip>

        <IconButton
          color="primary"
          aria-label="Close dialog"
          onClick={handleClosePreview}
          sx={{
            position: 'absolute',
            right: 16,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          {renderUpstreamSelector()}
          {renderTableContent()}
        </DialogContent>
      </Dialog>
    </Grid>
  );
};

AssociationFieldTableViewComponent.propTypes = {
  attribute: PropTypes.shape({}).isRequired,
  entityId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  entityData: PropTypes.shape({}),
};

export default AssociationFieldTableViewComponent;
